import { Select, MenuItem, FormControl, InputLabel, FormHelperText } from '@mui/material';
import { updateCommentThunk, getAllCommentsThunk } from '@redux/features/comment.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC, useEffect } from 'react';
import * as Yup from 'yup';

type IProps = {
    show: boolean;
    handleClose: () => void;
    commentId: number;
};

const UpdateCommentModal: FC<IProps> = ({ show, handleClose, commentId }) => {
    const { updateLoading, comments } = useAppSelector((state: RootState) => state.commentState);
    const [showFullComment, setShowFullComment] = React.useState<boolean>(false);
    // ----------------------------------------------------------------
    const dispatch = useAppDispatch();
    // --------------------------------------------------
    const validationSchema = Yup.object({
        isVisible: Yup.boolean().required('Visibility status is required'),
    });

    // Helper function to get visibility status
    const getVisibilityStatus = (comment: any) => {
        return comment?.isVisible || comment?.visible || false;
    };

    // Helper function to format date
    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    // Helper function to truncate comment
    const truncateComment = (text: string, maxLength: number = 150) => {
        if (!text) return '-';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    // Helper function to check if comment is long
    const isLongComment = (text: string, threshold: number = 150) => {
        return text && text.length > threshold;
    };

    // Find the comment by ID
    const comment = comments?.find((c: any) => c.id === commentId);
    const currentVisibility = comment ? getVisibilityStatus(comment) : false;

    const handleSubmit = (values: any) => {
        dispatch(updateCommentThunk({ commentId, isVisible: values.isVisible })).then((res: any) => {
            if (res.meta.requestStatus === 'fulfilled') {
                dispatch(getAllCommentsThunk());
                handleClose();
                formik.resetForm();
            }
        });
    };
    // --------------------------------------------------
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            isVisible: currentVisibility,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });
    // --------------------------------------------------
    useEffect(() => {
        if (comment && show) {
            formik.setValues({
                isVisible: getVisibilityStatus(comment),
            });
        }
    }, [comment, show]);

    return (
        <Modal
            show={show}
            onHide={() => handleClose()}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[500px] md:max-h-[80vh]"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">Update Comment</Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                {comment && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-md space-y-3">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">
                                <span className="font-semibold">Email:</span> {comment?.email || comment?.user?.email || '-'}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600 mb-1">
                                <span className="font-semibold">Comment:</span>
                            </p>
                            <div className="bg-white p-3 rounded border border-gray-200 mt-1">
                                <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                                    {showFullComment || !isLongComment(comment?.comment)
                                        ? comment?.comment || '-'
                                        : truncateComment(comment?.comment)}
                                </p>
                            </div>
                            {isLongComment(comment?.comment) && (
                                <button
                                    onClick={() => setShowFullComment(!showFullComment)}
                                    className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
                                >
                                    {showFullComment ? 'Show Less' : 'View Full Comment'}
                                </button>
                            )}
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">
                                <span className="font-semibold">Rate:</span> {comment?.rate || '-'} / 5
                            </p>
                        </div>
                        {/* Lifecycle Information */}
                        {(comment?.createdAt || comment?.updatedAt) && (
                            <div className="mt-3 pt-3 border-t border-gray-300">
                                <p className="text-xs font-semibold text-gray-700 mb-2">Lifecycle Information:</p>
                                <div className="space-y-1 text-xs text-gray-600">
                                    {comment?.createdAt && (
                                        <p>
                                            <span className="font-semibold">Created At:</span> {formatDate(comment.createdAt)}
                                        </p>
                                    )}
                                    {comment?.updatedAt && (
                                        <p>
                                            <span className="font-semibold">Updated At:</span> {formatDate(comment.updatedAt)}
                                        </p>
                                    )}
                                    {comment?.createdBy && (
                                        <p>
                                            <span className="font-semibold">Created By:</span> {comment.createdBy}
                                        </p>
                                    )}
                                    {comment?.updatedBy && (
                                        <p>
                                            <span className="font-semibold">Updated By:</span> {comment.updatedBy}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
                    <FormControl
                        fullWidth
                        size="small"
                        error={formik.touched.isVisible && Boolean(formik.errors.isVisible)}
                        disabled={updateLoading}
                    >
                        <InputLabel id="isVisible-label">Visibility Status</InputLabel>
                        <Select
                            labelId="isVisible-label"
                            id="isVisible"
                            name="isVisible"
                            label="Visibility Status"
                            value={formik.values.isVisible ? 'true' : 'false'}
                            onChange={(e) => {
                                formik.setFieldValue('isVisible', e.target.value === 'true');
                            }}
                            onBlur={formik.handleBlur}
                        >
                            <MenuItem value="true">Visible</MenuItem>
                            {/* <MenuItem value="false">Hidden</MenuItem> */}
                        </Select>
                        {formik.touched.isVisible && formik.errors.isVisible && (
                            <FormHelperText>{String(formik.errors.isVisible)}</FormHelperText>
                        )}
                    </FormControl>
                </form>
            </Modal.Body>
            <Modal.Footer className="flex items-center justify-center gap-2 mt-5">
                <ButtonBase
                    innerText="Cancel"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={() => {
                        handleClose();
                        formik.resetForm();
                    }}
                    disabled={updateLoading}
                />
                <ButtonBase
                    innerText="Update Comment"
                    className="bg-yellow-600 hover:bg-yellow-800 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={formik.handleSubmit}
                    disabled={updateLoading}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default UpdateCommentModal;

